import { createComponent } from "react-fela";
import { useEffect, useRef } from "react";
import { fluid } from "~/styleguide/utils";
import { fonts } from "~/styleguide/typography";
import { colors } from "~/styleguide";
import { parseHTML } from "~/modules/html";
import { textStyles } from "~/styleguide/typography";

export default ({ content, largeHeading, executeScripts = false }) => {
    const ref = useRef(null);
    const parsedContent = parseHTML(content);

    useEffect(() => {
        if (!executeScripts || !ref.current) return;

        const scripts = ref.current.querySelectorAll("script");
        scripts.forEach(script => {
            const executableScript = document.createElement("script");

            Array.prototype.slice.call(script.attributes).forEach(attribute => {
                executableScript.setAttribute(attribute.name, attribute.value);
            });

            executableScript.text = script.text || script.textContent || "";
            script.parentNode.replaceChild(executableScript, script);
        });
    }, [executeScripts, parsedContent]);

    return (
        <PostContent
            innerRef={ref}
            dangerouslySetInnerHTML={{ __html: parsedContent }}
            largeHeading={largeHeading}
        />
    );
};

const postContentHeadingStyles = {
    heading: {
        ...fonts.aeonikBold,
        lineHeight: "117%",
        scrollMarginTop: "100px",
        extend: [
            fluid("font-size", { max: 32, min: 24 }),
            fluid("margin-top", { max: 50, min: 30 })
        ],

        "& a": {
            color: colors.blue,
            ...fonts.logicalBold,
            transition: "opacity 0.3s ease",
            textDecoration: "none",
            whenActive: {
                opacity: 0.65
            }
        }
    }
};

const blockquoteStyles = {
    ...fonts.spectral,
    maxWidth: "92%",
    lineHeight: "1.4",
    extend: [
        fluid("font-size", { max: 32, min: 24 }),
        fluid("marginV", { max: 50, min: 30 })
    ]
};

const openingParagraphStyles = {
    ":first-child": {
        fontWeight: "normal",
        fontStyle: "normal",
        lineHeight: "125%",
        display: "block",
        extend: [
            fluid("font-size", { max: 36, min: 24 }),
            fluid("margin-bottom", { max: 25, min: 10 })
        ]
    }
};

const PostContent = createComponent(
    ({ largeHeading }) => ({
        "> *": {
            marginBottom: 0, // Reset default
            lineHeight: "125%",
            extend: [
                fluid("margin-top", { max: 25, min: 10 }),
                fluid("font-size", { max: 24, min: 16 })
            ],
            "& a": {
                color: colors.black,
                textDecoration: "underline",
                transition: "text-decoration-color 0.2s ease",

                textDecorationColor: colors.black,
                ":active": { textDecorationColor: colors.blue },
                ":hover": { textDecorationColor: colors.blue }
            },
            "&:first-child": {
                marginTop: 0
            }
        },
        "> h1": {
            extend: [postContentHeadingStyles.heading]
        },
        "> h2": {
            extend: [postContentHeadingStyles.heading]
        },
        "> h3": {
            extend: [postContentHeadingStyles.heading]
        },
        "> h4": {
            extend: [postContentHeadingStyles.heading]
        },
        "> h5": {
            extend: [postContentHeadingStyles.heading]
        },
        "> p": {
            letterSpacing: "0.5",
            lineHeight: "1.4",
            ":first-of-type": {
                "> em strong": openingParagraphStyles,
                "> strong em": openingParagraphStyles
            }
        },
        "> ul li": {
            marginBottom: 10,
            ":last-of-type": {
                marginBottom: 0
            }
        },
        "& img": {
            maxWidth: "100% !important",
            height: "auto"
        },
        "& .alignnone": {
            margin: "20px 20px 20px 0"
        },
        "& .aligncenter": {
            display: "block",
            margin: "20px auto 20px auto"
        },
        "& .alignright": {
            float: "right",
            margin: "20px 0 20px 20px",

            maxWidthMobile: {
                maxWidth: "50%"
            }
        },
        "& .alignleft": {
            float: "left",
            margin: "20px 20px 20px 0",
            maxWidthMobile: {
                maxWidth: "50%"
            }
        },
        "& .wp-caption": {
            padding: "20px 3px 10px",
            textAlign: "center",
            maxWidth: "100%"
        },
        "& .wp-caption-text": {
            fontSize: 16,
            lineHeight: 1.3,
            opacity: 0.7,
            fontStyle: "italic"
        },

        // Blockquotes
        "& blockquote": blockquoteStyles,
        "& blockquote p": blockquoteStyles,

        // Tables
        "> table": {
            width: "100%",

            "& tr": {
                "& td": {
                    fontSize: 16,
                    padding: 5
                },
                ":first-child": {
                    "> td": {
                        fontWeight: "bold"
                    }
                }
            }
        },

        extend: [
            {
                condition: !!largeHeading,
                style: {
                    "& h1": {
                        ...textStyles.heading1,
                        marginBottom: 40
                    }
                }
            }
        ]
    }),
    "div",
    ["dangerouslySetInnerHTML"]
);
